import { ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import type { ParsedChord } from "./parse-chord-symbol";
import type { PieceTonality } from "./piece-overview-data";

/**
 * Maps semitone offsets (root - tonic) to scale-degree numbers (1-7)
 * for the diatonic notes of each mode. Off-scale offsets return
 * `undefined` and produce `null` from `chordRomanNumeral`, callers
 * fall back to displaying the chord name only.
 */
const DEGREES_MAJOR: Record<number, number> = {
  0: 1,
  2: 2,
  4: 3,
  5: 4,
  7: 5,
  9: 6,
  11: 7,
};

const DEGREES_MINOR: Record<number, number> = {
  0: 1,
  2: 2,
  3: 3,
  5: 4,
  7: 5,
  8: 6,
  10: 7,
};

const ROMAN_UPPER = ["", "I", "II", "III", "IV", "V", "VI", "VII"];

const MINOR_QUALITIES = new Set([
  "minor",
  "m7",
  "min",
  "min7",
  "m6",
  "min6",
  "minmaj7",
  "m9",
  "m(add9)",
]);

const DIMINISHED_QUALITIES = new Set([
  "diminished",
  "dim7",
  "m7b5",
  "half-dim7",
]);

const AUGMENTED_QUALITIES = new Set(["augmented", "aug7"]);

/**
 * Best-effort conversion of a parsed chord into a roman numeral within
 * a given tonality. Returns `null` for chromatic / out-of-scale roots
 * or unknown qualities so the caller falls back to displaying just the
 * chord name. Always uppercase for major / dominant qualities,
 * lowercase for minor, lowercase + `°` for diminished, uppercase + `+`
 * for augmented.
 */
export function chordRomanNumeral(
  chord: ParsedChord,
  tonality: PieceTonality,
): string | null {
  const tonicPc = ROOT_PITCH_CLASS[tonality.tonic];
  const rootPc = ROOT_PITCH_CLASS[chord.root];

  if (tonicPc === undefined || rootPc === undefined) {
    return null;
  }

  const semitones = (((rootPc - tonicPc) % 12) + 12) % 12;
  const table = tonality.mode === "major" ? DEGREES_MAJOR : DEGREES_MINOR;
  const degree = table[semitones];

  if (degree === undefined) {
    return null;
  }

  const upper = ROMAN_UPPER[degree];

  if (DIMINISHED_QUALITIES.has(chord.quality)) {
    return `${upper.toLowerCase()}°`;
  }

  if (MINOR_QUALITIES.has(chord.quality)) {
    return upper.toLowerCase();
  }

  if (AUGMENTED_QUALITIES.has(chord.quality)) {
    return `${upper}+`;
  }

  return upper;
}
